import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { AlertTriangle, CheckCircle2, Circle, ArrowRight } from 'lucide-react';
import api from '../lib/api';

const URGENCY_STYLES = {
  high:   'border-destructive bg-destructive/5',
  normal: 'border-primary bg-primary/5',
  low:    'border-success bg-success/5',
  none:   'border-muted bg-muted/30',
};

const URGENCY_TEXT = {
  high:   'text-destructive',
  normal: 'text-[#333333]',
  low:    'text-success',
  none:   'text-muted-foreground',
};

export default function ParticipantProcessView({ participantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!participantId) return;
    api.get(`/participants/${participantId}/process-status`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [participantId]);

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6 pb-4">
          <Skeleton className="h-4 w-32 mb-4" />
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map(i => (
              <React.Fragment key={i}>
                <Skeleton className="h-9 w-9 rounded-full" />
                {i < 5 && <Skeleton className="h-1 flex-1" />}
              </React.Fragment>
            ))}
          </div>
          <Skeleton className="h-14 w-full mt-4" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { phases, current_phase, recommended_action, action_url, urgency, warnings } = data;

  return (
    <Card>
      <CardContent className="pt-5 pb-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          Var är vi i processen?
        </p>

        {/* Timeline */}
        <div className="flex items-center gap-0">
          {phases.map((phase, idx) => (
            <React.Fragment key={phase.phase}>
              {/* Phase circle */}
              <div className="flex flex-col items-center gap-1 min-w-0">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
                    phase.done
                      ? 'bg-success border-success text-white'
                      : phase.active
                      ? 'bg-primary border-primary text-[#333333]'
                      : 'bg-background border-muted text-muted-foreground'
                  }`}
                >
                  {phase.done ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <span>{phase.phase}</span>
                  )}
                </div>
                <span
                  className={`text-[10px] text-center leading-tight whitespace-nowrap ${
                    phase.active ? 'font-semibold text-[#333333]' : 'text-muted-foreground'
                  }`}
                >
                  {phase.label}
                </span>
              </div>

              {/* Connector */}
              {idx < phases.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-1 mb-4 ${
                    phases[idx + 1].done || phases[idx + 1].active
                      ? 'bg-primary'
                      : 'bg-muted'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Action card */}
        {recommended_action && (
          <div className={`mt-4 rounded-lg border p-3 flex items-start gap-2 ${URGENCY_STYLES[urgency] || URGENCY_STYLES.none}`}>
            {urgency === 'high' ? (
              <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${URGENCY_TEXT[urgency]}`} />
            ) : (
              <ArrowRight className={`w-4 h-4 mt-0.5 shrink-0 ${URGENCY_TEXT[urgency]}`} />
            )}
            <span className={`text-sm font-medium ${URGENCY_TEXT[urgency]}`}>
              {recommended_action}
            </span>
          </div>
        )}

        {/* Warnings */}
        {warnings && warnings.length > 0 && (
          <ul className="mt-2 space-y-1">
            {warnings.map((w, i) => (
              <li key={i} className="flex items-center gap-1.5 text-xs text-amber-700">
                <AlertTriangle className="w-3 h-3 shrink-0" />
                {w}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
